import { useParams } from 'react-router-dom';
import { useCallback, useMemo, useState, useEffect } from 'react';

import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { LoadedData } from '@occc-app/routes/Wallet/types';
import {
  SegmentFragment,
  SegmentUploadedFileFragment,
  Segment_Upload_File_Status,
} from '@occc/graphql';
import { useIsUserPermittedTo } from '@occc/utils-access-control';
import { Feature } from '@occc/utils-access-control';

import { SegmentDetailsPage } from './segment-details.page';

import { SegmentFormValues } from '../../segments.types';
import {
  SEGMENT_CSV_FILES_PAGE_SIZE,
  SEGMENT_CSV_FILES_BEFORE_PARAM,
  SEGMENT_CSV_FILES_AFTER_PARAM,
} from '../../segments.constants';
import { SegmentUpdateDetailsModal } from '../segment-update-details-modal/segment-update-details.modal';
import { useSegmentUpdateController } from '../segment-update-details-modal/use-segment-update-details-controller.hook';
import { useGetSegmentUploadedFiles } from '../../hooks/use-get-member-dossier-segment-uploaded-files.hook';
import { useGetSegmentById } from '../../hooks/use-get-segment-by-id.hook';
import { useSegmentMembershipCountPolling } from '../../hooks/use-segment-membership-count-polling.hook';

export const SegmentDetailsController = () => {
  const { uuid = '' } = useParams<{ uuid: string }>();

  const [isEditModalShown, setIsEditModalShown] = useState(false);
  const [isAddingOrRemovingInProgress, setIsAddingOrRemovingInProgress] = useState(false);
  const [shouldPollUploadedFiles, setShouldPollUploadedFiles] = useState(false);
  const [shouldPollSegment, setShouldPollSegment] = useState(false);

  const isUserPermittedToEditSegment = useIsUserPermittedTo(Feature.SEGMENT_EDIT);

  const [csvFilesPaginationInfo, onCsvFilesPageChange] = usePaginationParams({
    pageSize: SEGMENT_CSV_FILES_PAGE_SIZE,
    beforeParamName: SEGMENT_CSV_FILES_BEFORE_PARAM,
    afterParamName: SEGMENT_CSV_FILES_AFTER_PARAM,
  });

  const uploadedFilesResponse = useGetSegmentUploadedFiles({
    segmentUuid: uuid,
    onPageChange: onCsvFilesPageChange,
    paginationInfo: csvFilesPaginationInfo,
    isPollingEnabled: shouldPollUploadedFiles,
  });

  const uploadedFilesData: LoadedData<SegmentUploadedFileFragment[]> = useMemo(() => {
    return {
      data: uploadedFilesResponse.data?.segmentUploadedFiles.edges.map((edge) => edge.node),
      loading: uploadedFilesResponse.loading,
      error: uploadedFilesResponse.error,
    };
  }, [
    uploadedFilesResponse.data?.segmentUploadedFiles.edges,
    uploadedFilesResponse.error,
    uploadedFilesResponse.loading,
  ]);

  const hasIncompleteFiles = useMemo(() => {
    return (
      uploadedFilesData.data?.some(
        (file) =>
          file.status !== Segment_Upload_File_Status.Completed &&
          file.status !== Segment_Upload_File_Status.Failed,
      ) ?? false
    );
  }, [uploadedFilesData.data]);

  useEffect(() => {
    setShouldPollUploadedFiles(hasIncompleteFiles);
  }, [hasIncompleteFiles]);

  const segmentResponse = useGetSegmentById({
    idEquals: uuid,
    isPollingEnabled: shouldPollSegment,
  });

  const { shouldPollSegment: shouldPollMembershipCount, onMembershipsChanged } =
    useSegmentMembershipCountPolling({
      membershipCount: segmentResponse.data?.membershipCount,
      hasIncompleteFiles,
      refetchSegment: segmentResponse.refetch,
    });

  useEffect(() => {
    setShouldPollSegment(shouldPollMembershipCount);
  }, [shouldPollMembershipCount]);

  const { isUpdateInProgress, onUpdateSubmit, initialFormValues } = useSegmentUpdateController({
    segment: segmentResponse.data || undefined,
  });

  const segmentData: LoadedData<SegmentFragment> = useMemo(() => {
    return {
      data: segmentResponse.data || undefined,
      loading: (segmentResponse.loading && segmentResponse.isInitialLoading) || isUpdateInProgress,
      error: segmentResponse.error,
    };
  }, [
    segmentResponse.data,
    segmentResponse.error,
    segmentResponse.loading,
    segmentResponse.isInitialLoading,
    isUpdateInProgress,
  ]);

  const onEditClick = useCallback(() => {
    setIsEditModalShown(true);
  }, []);

  const onEditModalClose = useCallback(() => {
    setIsEditModalShown(false);
  }, []);

  const onUpdate = useCallback(
    async (values: SegmentFormValues) => {
      await onUpdateSubmit(values);
      onEditModalClose();
    },
    [onUpdateSubmit, onEditModalClose],
  );

  return (
    <>
      <SegmentDetailsPage
        isEditAllowed={isUserPermittedToEditSegment}
        segmentUuid={uuid}
        segmentData={segmentData}
        uploadedFilesData={uploadedFilesData}
        uploadedFilesPaginationProps={uploadedFilesResponse.paginationProps}
        isAddingOrRemovingInProgress={isAddingOrRemovingInProgress}
        onAssignMembershipsLoadingStateChange={setIsAddingOrRemovingInProgress}
        onMembershipsChanged={onMembershipsChanged}
        onEditClick={onEditClick}
        hasIncompleteFiles={hasIncompleteFiles}
        isMembershipCountUpdating={shouldPollMembershipCount}
      />

      {isEditModalShown && (
        <SegmentUpdateDetailsModal
          onClose={onEditModalClose}
          isUpdateInProgress={isUpdateInProgress}
          onSubmit={onUpdate}
          initialFormValues={initialFormValues}
        />
      )}
    </>
  );
};
